import React, { useState } from 'react';
import { BROKERS } from '../config/brokers';
import { setAuthSession } from '../utils/auth';

// ── Dev-only bypass ────────────────────────────────────────────────────────
// When running locally (npm start), show a broker picker instead of Google OAuth.
// On production this code path is never reached.
const IS_DEV = process.env.NODE_ENV === 'development';

function DevLogin() {
  function pick(broker) {
    setAuthSession({
      brokerName: broker.name,
      email:      `${broker.email}@sterlingcreadvisors.com`,
      name:       broker.name,
      isAdmin:    broker.isAdmin || false,
      picture:    '',
    });
    window.location.reload();
  }

  return (
    <div style={{
      fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
      minHeight: '100vh', backgroundColor: '#f5f4f1',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', padding: '24px',
    }}>
      <div style={{
        backgroundColor: '#fff', borderRadius: '20px',
        padding: '32px', maxWidth: '380px', width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}>
        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px' }}>
          Local Dev
        </div>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 20px' }}>
          Sign in as…
        </h1>
        {BROKERS.map((broker) => (
          <button
            key={broker.name}
            onClick={() => pick(broker)}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px',
              width: '100%', padding: '12px 14px',
              backgroundColor: '#f5f4f1', border: '1px solid #e8e6e0',
              borderRadius: '10px', marginBottom: '8px',
              cursor: 'pointer', fontSize: '14px', fontWeight: '600',
              color: '#111827', fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              backgroundColor: '#EEEDFE', color: '#3C3489',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '11px', fontWeight: '700', flexShrink: 0,
            }}>
              {broker.initials}
            </div>
            <div>
              <div>{broker.name}</div>
              {broker.isAdmin && <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '400' }}>Admin</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

const C = {
  indigo:       '#534AB7',
  indigoDark:   '#3C3489',
  indigoLight:  '#EEEDFE',
  teal:         '#1D9E75',
  bgPage:       '#f5f4f1',
  bgCard:       '#ffffff',
  border:       '#e8e6e0',
  textPrimary:  '#111827',
  textSecondary:'#6b7280',
  red:          '#DC2626',
  redLight:     '#FEF2F2',
  redBorder:    '#FECACA',
};

const ERROR_MESSAGES = {
  wrong_domain: 'Only @sterlingcreadvisors.com accounts can access this app.',
  not_found:    'Your Sterling email isn\'t linked to a broker profile yet. Contact Sara.',
  token_failed: 'Authentication failed. Please try again.',
  no_code:      'Authentication cancelled. Please try again.',
  default:      'Something went wrong. Please try again.',
};

export default function Login({ authError }) {
  const [loading, setLoading] = useState(false);

  if (IS_DEV) return <DevLogin />;

  function handleSignIn() {
    setLoading(true);
    window.location.href = '/api/auth';
  }

  const errorMsg = authError
    ? (ERROR_MESSAGES[authError] || ERROR_MESSAGES.default)
    : null;

  return (
    <div style={{
      fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
      minHeight: '100vh',
      backgroundColor: C.bgPage,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        backgroundColor: C.bgCard,
        borderRadius: '20px',
        padding: '40px 32px',
        maxWidth: '380px',
        width: '100%',
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        textAlign: 'center',
      }}>
        {/* Logo / wordmark */}
        <div style={{
          fontSize: '13px', fontWeight: '800', letterSpacing: '0.12em',
          textTransform: 'uppercase', color: C.indigo,
          marginBottom: '6px',
        }}>
          Sterling CRE
        </div>
        <div style={{
          fontSize: '11px', fontWeight: '500', letterSpacing: '0.08em',
          textTransform: 'uppercase', color: C.textSecondary,
          marginBottom: '32px',
        }}>
          Prospect + Nurture
        </div>

        <h1 style={{
          fontSize: '22px', fontWeight: '700', color: C.textPrimary,
          margin: '0 0 8px',
        }}>
          Welcome back
        </h1>
        <p style={{
          fontSize: '14px', color: C.textSecondary,
          margin: '0 0 28px', lineHeight: '1.5',
        }}>
          Sign in with your Sterling Google account to access your queue.
        </p>

        {errorMsg && (
          <div style={{
            background: C.redLight, border: `1px solid ${C.redBorder}`,
            borderRadius: '10px', padding: '12px 14px',
            fontSize: '13px', color: C.red,
            marginBottom: '20px', textAlign: 'left',
          }}>
            {errorMsg}
          </div>
        )}

        <button
          onClick={handleSignIn}
          disabled={loading}
          style={{
            width: '100%', padding: '14px 20px',
            backgroundColor: loading ? '#9CA3AF' : C.indigo,
            color: '#fff', border: 'none', borderRadius: '12px',
            fontSize: '15px', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: 'inherit',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            transition: 'background-color 0.15s',
          }}
        >
          {/* Google G icon */}
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#fff" fillOpacity=".9"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#fff" fillOpacity=".8"/>
            <path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#fff" fillOpacity=".7"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#fff" fillOpacity=".9"/>
          </svg>
          {loading ? 'Redirecting…' : 'Sign in with Google'}
        </button>

        <p style={{
          fontSize: '12px', color: C.textSecondary,
          marginTop: '20px', lineHeight: '1.5',
        }}>
          Only @sterlingcreadvisors.com accounts are permitted.
        </p>
      </div>
    </div>
  );
}
